import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router';
import './PrivacyPolicy.scss';

const privacySchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name: 'Privacy Policy',
  url: 'https://www.presstoindia.com/privacy-policy',
  description: 'How Pressto India collects, uses, and protects personal information on presstoindia.com.',
  publisher: {
    '@type': 'Organization',
    name: 'Pressto India',
    url: 'https://www.presstoindia.com/',
  },
};

const PrivacyPolicy = () => {
  return (
    <div className="privacy-page">
      <Helmet>
        <title>Privacy Policy | Pressto India</title>
        <meta
          name="description"
          content="Read how Pressto India collects, uses, and protects personal information when you use our website, stores, and pickup services."
        />
        <link rel="canonical" href="https://www.presstoindia.com/privacy-policy" />
        <script type="application/ld+json">{JSON.stringify(privacySchema)}</script>
      </Helmet>

      <article className="privacy-content">
        <h1>Privacy Policy</h1>
        <p className="privacy-updated">Last updated: 9 October 2026</p>
        <p>
          Pressto India (“Pressto”, “we”, “us”) provides premium laundry, dry cleaning, and shoe and bag care. This policy explains what personal information we collect through presstoindia.com, our stores, pickup and drop services, and related customer support, and how we use it.
        </p>

        <h2>Information we collect</h2>
        <p>We collect information you give us and information generated when you use the website.</p>
        <ul>
          <li>
            <strong>Contact and updates.</strong> Name and email address when you use the footer contact form.
          </li>
          <li>
            <strong>Pickup and service requests.</strong> First name, last name, mobile number, email address, city, PIN code, street address, and preferred pickup time when you book a pickup or create an account.
          </li>
          <li>
            <strong>Service records.</strong> Order and visit details linked to your customer profile, including the payment mode recorded for a service.
          </li>
          <li>
            <strong>Messages you send us.</strong> Information you share by phone, email, or WhatsApp when you contact us.
          </li>
          <li>
            <strong>Website activity.</strong> Pages viewed, device and browser details, and approximate location derived from your IP address, collected through cookies and similar tools.
          </li>
        </ul>

        <h2>How we use your information</h2>
        <ul>
          <li>To schedule pickups, process garments and accessories, and deliver completed orders.</li>
          <li>To create and manage your customer profile and show your service history.</li>
          <li>To reply to enquiries and send service-related calls, SMS, email, or WhatsApp messages.</li>
          <li>To send updates you asked to receive through the website contact form.</li>
          <li>To understand how the website is used and to improve our stores and services.</li>
          <li>To keep the website secure and meet legal and accounting requirements.</li>
        </ul>

        <h2>How we share information</h2>
        <p>We do not sell your personal information. We share it only when it is needed to run the service:</p>
        <ul>
          <li>With Pressto stores and staff who handle your order.</li>
          <li>With service providers that send email on our behalf, host our systems, or measure website use. These include EmailJS for form messages, Google Tag Manager, and Microsoft Clarity.</li>
          <li>With WhatsApp when you choose to message us there.</li>
          <li>When the law requires us to disclose information, or when it is needed to protect our customers, staff, or business.</li>
        </ul>

        <h2>Cookies and similar technologies</h2>
        <p>
          The website uses cookies and similar technologies to remember basic site behaviour and to measure traffic. Google Tag Manager and Microsoft Clarity help us see which pages are used and where visitors have trouble. You can control cookies in your browser settings. Blocking cookies may limit some site features.
        </p>

        <h2>How long we keep information</h2>
        <p>
          We keep personal information for as long as your customer relationship continues and for the period needed to complete orders, handle claims, and meet tax and legal record-keeping duties. When information is no longer needed, we delete it or remove details that identify you.
        </p>

        <h2>How we protect information</h2>
        <p>
          We use access controls and service-provider agreements to limit who can see customer information. No method of transmission or storage is completely secure, so we cannot guarantee absolute security.
        </p>

        <h2>Your choices and rights</h2>
        <p>You may ask us to:</p>
        <ul>
          <li>Confirm what personal information we hold about you.</li>
          <li>Correct information that is inaccurate.</li>
          <li>Delete information we no longer need to provide a service or keep by law.</li>
          <li>Stop sending promotional updates. Service messages about an active order may still be sent.</li>
        </ul>
        <p>
          To make a request, email <a href="mailto:info@presstoindia.com">info@presstoindia.com</a> or call <a href="tel:1800229199">1800229199</a>. We may need to confirm your identity before we act on the request.
        </p>

        <h2>Children</h2>
        <p>
          Our services are meant for adults. We do not knowingly collect personal information from children. If you believe a child has given us personal information, contact us and we will delete it.
        </p>

        <h2>Changes to this policy</h2>
        <p>
          We may update this policy when our services or legal duties change. The date at the top of this page shows when it was last revised. Continued use of the website after an update means you have seen the revised policy.
        </p>

        <h2>Contact us</h2>
        <p>
          Pressto India<br />
          Head office, Lower Parel, Mumbai 400013<br />
          Email: <a href="mailto:info@presstoindia.com">info@presstoindia.com</a><br />
          Phone: <a href="tel:1800229199">1800229199</a><br />
          Website: <Link to="/">www.presstoindia.com</Link>
        </p>
      </article>
    </div>
  );
};

export default PrivacyPolicy;
